<template>
  <div class="flex items-center min-w-0 flex-wrap gap-y-2" data-testid="approval-progress-bar">
    <template v-for="(layer, layerIdx) in visualLayers" :key="layer.order">
      <div
        v-if="layerIdx > 0"
        class="h-[2px] w-3 shrink-0 rounded-full"
        :class="connectorColor(layerIdx)"
        data-testid="approval-progress-connector"
      />

      <div
        :class="[
          'flex items-center shrink-0',
          layer.isGrouped
            ? 'relative gap-1 px-1.5 pb-1 pt-3 rounded-lg border-2 border-primary30'
            : ''
        ]"
        :data-testid="layer.isGrouped ? 'approval-progress-group' : undefined"
      >
        <span
          v-if="layer.isGrouped"
          class="absolute -top-2 left-1/2 -translate-x-1/2 text-[8px] font-bold leading-none px-1.5 py-0.5 bg-primary30 text-white rounded select-none"
          data-testid="approval-progress-operator"
        >
          {{ layer.operator === 'OR' ? 'OU' : 'E' }}
        </span>

        <template v-for="(role, roleIdx) in layer.roles" :key="`${layerIdx}-${role.role}`">
          <div
            v-if="roleIdx > 0 && layer.isGrouped"
            class="h-[2px] w-2 shrink-0 rounded-full bg-gray-200"
          />

          <v-tooltip :text="tooltipText(role)" location="top">
            <template #activator="{ props: tooltipProps }">
              <div
                v-bind="tooltipProps"
                class="relative flex items-center justify-center w-[22px] h-[22px] rounded-full cursor-help shrink-0 text-white"
                :class="dotClasses(role.state)"
                data-testid="approval-progress-step"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-3 h-3"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path :d="role.state === 'rejected' ? mdiClose : mdiCheck" />
                </svg>
              </div>
            </template>
          </v-tooltip>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiCheck, mdiClose } from '@mdi/js'
import type { ApprovalInstanceDto } from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { ValidationRole, VALIDATION_ROLE_LABELS } from '@/types/approval-flow.types'

type RoleState = 'approved' | 'rejected' | 'pending-active' | 'pending-future'

interface VisualRole {
  role: string
  label: string
  state: RoleState
  deciderName: string | null
}

interface VisualLayer {
  order: number
  operator: 'AND' | 'OR'
  isGrouped: boolean
  roles: VisualRole[]
}

const props = defineProps<{
  approvalInstance: ApprovalInstanceDto
}>()

const visualLayers = computed<VisualLayer[]>(() => {
  const { layers, decisions, currentLayerOrder, status } = props.approvalInstance
  const sortedLayers = [...layers].sort((a, b) => a.order - b.order)

  return sortedLayers.map((layer) => {
    const layerDecisions = decisions.filter((d) => d.layerOrder === layer.order)

    const roles: VisualRole[] = layer.roles.map((lr) => {
      const decision = layerDecisions.find((d) => d.role === lr.role)
      let state: RoleState

      if (decision?.decision === 'APPROVED') {
        state = 'approved'
      } else if (decision?.decision === 'REJECTED') {
        state = 'rejected'
      } else if (layer.order <= currentLayerOrder && status === 'IN_PROGRESS') {
        state = 'pending-active'
      } else if (status === 'COMPLETED') {
        state = 'approved'
      } else {
        state = 'pending-future'
      }

      return {
        role: lr.role,
        label: VALIDATION_ROLE_LABELS[lr.role as ValidationRole] ?? lr.role,
        state,
        deciderName: decision?.userName ?? null
      }
    })

    return {
      order: layer.order,
      operator: layer.operator,
      isGrouped: layer.roles.length > 1,
      roles
    }
  })
})

function dotClasses(state: RoleState): string {
  switch (state) {
    case 'approved':
      return 'bg-negative'
    case 'rejected':
      return 'bg-primary30'
    case 'pending-active':
      return 'bg-gray-400'
    case 'pending-future':
      return 'bg-gray-400'
  }
}

function connectorColor(layerIdx: number): string {
  const prevLayer = visualLayers.value[layerIdx - 1]
  if (!prevLayer) return 'bg-gray-200'

  const allApproved = prevLayer.roles.every((r) => r.state === 'approved')
  if (allApproved) return 'bg-green-400'

  const hasRejected = prevLayer.roles.some((r) => r.state === 'rejected')
  if (hasRejected) return 'bg-red-400'

  return 'bg-gray-200'
}

function stateLabel(state: RoleState): string {
  switch (state) {
    case 'approved':
      return 'Aprovado'
    case 'rejected':
      return 'Rejeitado'
    case 'pending-active':
      return 'Aguardando decisão'
    case 'pending-future':
      return 'Pendente'
  }
}

function tooltipText(role: VisualRole): string {
  const status = stateLabel(role.state)
  if (role.deciderName) {
    return `${role.label}: ${status} por ${role.deciderName}`
  }
  return `${role.label}: ${status}`
}
</script>
